import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener } from 'ahooks';
import { Input, NumberInput } from '@mantine/core';
import { translate } from '@shakkerai/util-i18n';
import { InputTag } from '@arco-design/web-react';
import CustomPromptItem from './prompt-item';
import { Icon } from '@shakkerai/components';
import { useDebounceFn } from 'ahooks';
import { setTempState } from '@starflow/app/store';
import { updateUnitData } from '@starflow/app/helpers/flow';

import '../reference.scss';
const TokenSeparators = [',', '，'];
const str2Arr = (prompt = '') => {
  const regex = new RegExp(`[${TokenSeparators.join('')}]`);
  const values = prompt.split(regex) || [];
  return values.filter((value) => value && value !== ' ');
};
const PromptWeightSettings = React.memo(({ generator }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const { prompt } = generator.data;
  const promptInputRef = useRef(null);
  const [itemValue, setItemValue] = useState('');
  const [itemWeight, setItemWeight] = useState(1.2);
  // const [valueTags, setValueTags] = useState([]);
  const handleItemValueChange = (event) => {
    const inputValue = event.target.value;
    setItemValue(inputValue);
  };
  const handleInputScaleChange = (val) => {
    setItemWeight(Number(Number(val).toFixed(1)));
  };

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const { run: updatePrompt } = useDebounceFn(
    (inputVal = '') => {
      updateUnitData(generator.id, { prompt: inputVal });
    },
    { wait: 500 },
  );

  const handleAddItem = () => {
    if (!itemValue) {
      return;
    }
    let text = itemValue;
    if (itemWeight) {
      text = `(${itemValue}: ${itemWeight})`;
    }
    updatePrompt(`${text}, ${prompt}`);
  };

  const resetAndSaveData = useCallback(() => {
    setItemValue('');
    setItemWeight(0);
    // setValueTags([]);
  }, []);

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-role="btn-reload-cn-image"]')) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="prompt-weight-settings"]')) {
      return;
    }

    if (event.target?.closest('.mantine-NumberInput-control')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    // 关闭之前做清理和保存工作
    resetAndSaveData();

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const entryElementDOM = document.getElementById(`prompt-weight-btn-${generator.id}`);

    if (!entryElementDOM) return;

    const { x, y } = entryElementDOM.getBoundingClientRect();
    let position = { x: x, y: y - 430 };

    setPosition(position);
  }, [generator.id]);

  const handleScaleChange = (idx, value, scale) => {
    const values = str2Arr(prompt);
    const origin = values[idx];
    let target = '';
    if (/\(/.test(origin)) {
      target = origin.replace(/\d+\.\d+/, Number(scale).toFixed(1));
    } else {
      target = `(${origin}: ${Number(scale).toFixed(1)})`;
    }
    values[idx] = target;
    updatePrompt(values.join(','));
  };
  const newTagRender = (props, index) => {
    return <CustomPromptItem {...props} index={index} onScaleChange={handleScaleChange} />;
  };

  const valueTags = useMemo(() => {
    const values = str2Arr(prompt);
    return values;
  }, [prompt]);

  const handleValuesChange = (values) => {
    // setValueTags(values);
    const promptVal = (values || []).join(',');
    updateUnitData(generator.id, { prompt: promptVal });
  };

  if (!position) {
    return null;
  }

  return createPortal(
    <Rnd
      scale={1}
      default={position}
      resizeHandleWrapperClass="hidden"
      dragHandleClassName="sub-drag-handle"
      className={`z-[101] image-reference-popup-wrap`}
    >
      <div ref={containerRef} className="z-20 w-[520px] bg-white shadow-light rounded-sm">
        <div className="sub-drag-handle flex items-center justify-between px-[16px] py-[12px] cursor-move border-b border-b-[#EFEFEF]">
          <span className="text-[#2d2d2d] font-bold font-grotesk text-sm">{translate('prompt_weight_title')}</span>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#2d2d2d]" />
          </button>
        </div>
        <div className="px-[16px] py-[16px] prompt-weight-window">
          <div className="flex">
            <div className="w-[426px]  bg-[#F9FBFB] rounded-sm flex items-center">
              <Input placeholder={translate('prompt_weight_placeholder')} onChange={handleItemValueChange} />
              <div className="h-[14px] w-[1px] ml-[16px] mr-[8px] bg-[#D9D9D9]"></div>
              <NumberInput
                min={0.1}
                defaultValue={1.2}
                leftSection={
                  <span style={{ display: 'inline-block', color: '#000' }}>{translate('prompt_weight')}</span>
                }
                decimalScale={1}
                fixedDecimalScale
                className={'prompt-input-numbers px-1 h-9'}
                max={3}
                step={0.1}
                onChange={handleInputScaleChange}
              />
            </div>
            <button
              className="cursor-pointer text-[#2d2d2d] ml-[12px] flex items-center hover:text-[#3d3d3d]"
              onClick={handleAddItem}
            >
              <Icon fontSize={18} className="shk-zoomin" color="inherit" />
              <span className="text-[14px] ml-[4px] translate-y-px">{translate('add_text')}</span>
            </button>
          </div>
          <div className="mt-[12px]" onClick={(e) => e.stopPropagation()}>
            <InputTag
              ref={promptInputRef}
              placeholder={translate('prompt_placeholder')}
              value={valueTags}
              inputValue=""
              onChange={handleValuesChange}
              // inputValue={prompt}
              dragToSort={true}
              saveOnBlur={true}
              // labelInValue={true}
              renderTag={newTagRender}
              tokenSeparators={TokenSeparators}
              // disabled={true}
              className={`prompt-input-main w-full overflow-y-auto bg-[#F9FBFB] px-[12px] py-[12px] select-auto resize-none text-[#2D2D2D] placeholder:text-[#999999] text-[14px] leading-[24px] caret-primary scrollbar-hidden`}
              style={{ maxHeight: 300, minHeight: 150 }}
            />
          </div>
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});

export default PromptWeightSettings;
